/**
 * Colores y orden canónico de las secciones (tipos de club).
 *
 * Paleta OFICIAL armonizada con el estilo indigo/fuchsia de la app (2026-08-04):
 *  - Aventureros → azul índigo (fondo #EEF3FC · activo #3E5AAB con texto blanco)
 *  - Conquistadores → rojo con dorado (fondo #FFF1ED · activo #C93E27 con texto dorado #F4C842)
 *  - Guías Mayores → azul marino con dorado (fondo #EEF4F7 · activo #1F4A66 con texto dorado #F0D94C)
 *
 * Orden visual siempre: Aventureros → Conquistadores → Guías Mayores.
 * Este orden refleja la trayectoria natural del miembro por edad y facilita
 * la lectura de reportes/UI de asociación (los admins escanean izquierda→derecha).
 */

import type { ClubType } from "@/types";

export const SECTION_ORDER: ClubType[] = [
  "aventureros",
  "conquistadores",
  "guias_mayores",
];

const ORDER_INDEX: Record<ClubType, number> = {
  aventureros: 0,
  conquistadores: 1,
  guias_mayores: 2,
};

/** Ordena una lista de secciones en el orden canónico. */
export function sortSections<T extends string>(sections: readonly T[]): T[] {
  return [...sections].sort((a, b) => {
    const ai = ORDER_INDEX[a as ClubType] ?? 99;
    const bi = ORDER_INDEX[b as ClubType] ?? 99;
    return ai - bi;
  });
}

/**
 * Paleta HEX oficial armonizada. Uso: styles inline, gradients dinámicos, SVG,
 * y como fuente base cuando Tailwind arbitrary values no aplican.
 */
export const SECTION_PALETTE: Record<
  ClubType,
  {
    background: string;
    color: string;
    border: string;
    hover: string;
    activeBackground: string;
    activeColor: string;
  }
> = {
  aventureros: {
    background: "#EEF3FC",
    color: "#2E4187",
    border: "#4A6DB2",
    hover: "#DCE6F5",
    activeBackground: "#3E5AAB",
    activeColor: "#FFFFFF",
  },
  conquistadores: {
    background: "#FFF1ED",
    color: "#A9251D",
    border: "#D13B2F",
    hover: "#FFE1DA",
    activeBackground: "#C93E27",
    activeColor: "#F4C842",
  },
  guias_mayores: {
    background: "#EEF4F7",
    color: "#1A4966",
    border: "#2C5A78",
    hover: "#DDEBF1",
    activeBackground: "#1F4A66",
    activeColor: "#F0D94C",
  },
};

/**
 * Chip fuerte (badge sólido). Uso principal en listados/badges. Aplica el
 * `activeBackground` + `activeColor` de la paleta oficial mediante Tailwind
 * arbitrary values.
 */
export const SECTION_CHIP_CLASSES: Record<ClubType, string> = {
  aventureros: "bg-[#3E5AAB] text-white",
  conquistadores: "bg-[#C93E27] text-[#F4C842]",
  guias_mayores: "bg-[#1F4A66] text-[#F0D94C]",
};

/**
 * Chip suave (fondo pastel + texto oscuro + borde). Para tablas, filtros
 * discretos y estados no seleccionados.
 */
export const SECTION_CHIP_SOFT_CLASSES: Record<ClubType, string> = {
  aventureros:
    "bg-[#EEF3FC] text-[#2E4187] border border-[#4A6DB2] hover:bg-[#DCE6F5]",
  conquistadores:
    "bg-[#FFF1ED] text-[#A9251D] border border-[#D13B2F] hover:bg-[#FFE1DA]",
  guias_mayores:
    "bg-[#EEF4F7] text-[#1A4966] border border-[#2C5A78] hover:bg-[#DDEBF1]",
};

/** Card border+background para tarjetas grandes de sección. */
export const SECTION_CARD_CLASSES: Record<ClubType, string> = {
  aventureros: "bg-[#EEF3FC] border-[#4A6DB2]",
  conquistadores: "bg-[#FFF1ED] border-[#D13B2F]",
  guias_mayores: "bg-[#EEF4F7] border-[#2C5A78]",
};

/** Gradient para headers/iconos redondeados grandes. */
export const SECTION_GRADIENT_CLASSES: Record<ClubType, string> = {
  aventureros: "from-[#4A6DB2] to-[#2E4187]",
  conquistadores: "from-[#D13B2F] to-[#A9251D]",
  guias_mayores: "from-[#2C5A78] to-[#1A4966]",
};

/** Color base HEX para acentos (progreso, iconos, gráficos). */
export const SECTION_ACCENT_HEX: Record<ClubType, string> = {
  aventureros: "#3E5AAB",
  conquistadores: "#C93E27",
  guias_mayores: "#1F4A66",
};
